import axios from 'axios'
import loginStore from '@/store/modules/login'
import router from '@/router/index'
import {
  build401LoginLocation,
  clearExpiredLoginState,
  shouldSkip401Redirect
} from '@/js/auth-redirect'
import { buildPostConfig } from '@/js/http-config'

export const HttpManager = {
  Init: function (env, rootStore) {
    axios.defaults.baseURL = env.VUE_APP_WEB_SERVER_URL // http://localhost:7000  // http://192.168.1.30
    // L2 fix：後端認證純 Bearer token，不需帶 cookie。withCredentials=true 會觸發嚴格 CORS
    // 且未來若有人誤加 cookie auth 就會出現 CSRF 風險，改為預設 false
    axios.defaults.withCredentials = false

    // Include the Authentication header when using JWT authentication
    axios.interceptors.request.use((request) => {
      if (loginStore.state.Jwt) {
        request.headers['Authorization'] = 'Bearer ' + loginStore.state.Jwt // store.state.login.Jwt
      }
      return request
    })

    // 異常處理
    axios.interceptors.response.use(
      (response) => {
        return response
      },
      (err) => {
        if (err && err.response) {
          switch (err.response.status) {
            case 401:
              if (shouldSkip401Redirect(err.config && err.config.url)) {
                break
              }
              clearExpiredLoginState(
                loginStore.state,
                window.localStorage,
                () => rootStore && rootStore.dispatch('permission/reset'))
              router.push(build401LoginLocation(
                router.currentRoute,
                router.authPendingRoute
              ), () => {}, () => {})
              break
            case 404:
              console.log('找不到該頁面')
              break
            case 500:
              console.log('伺服器出錯')
              break
            case 503:
              console.log('服務失效')
              break
            default:
              console.log(`連接錯誤${err.response.status}`)
          }
        } else {
          if (err.message === 'cancel') {
            console.log('取消請求')
          } else {
            console.log('連接到服務器失敗')
          }
        }
        return Promise.resolve(err.response)
      }
    )
  },
  GetBaseUrl: function () {
    return axios.defaults.baseURL
  }
}

export async function get (
  url,
  params = {},
  cancelToken = null,
  responseType = '',
  contentType = 'application/json',
  timeout = 0
) {
  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        params: params,
        headers: {
          'content-type': contentType
        },
        cancelToken: cancelToken,
        responseType: responseType,
        timeout: timeout
      })
      .then((response) => {
        resolve(response)
      })
      .catch((err) => {
        reject(err)
      })
  })
}

export async function post (
  url,
  data = {},
  cancelToken = null,
  responseType = '',
  contentType = 'application/json',
  params = {},
  onUploadProgress = null,
  timeout = 0
) {
  return new Promise((resolve, reject) => {
    axios
      .post(url, data, buildPostConfig({
        params,
        cancelToken,
        responseType,
        contentType,
        onUploadProgress,
        timeout
      }))
      .then(
        (response) => {
          resolve(response)
        },
        (err) => {
          reject(err)
        }
      )
  })
}

export async function put (
  url,
  data = {},
  cancelToken = null,
  responseType = '',
  contentType = 'application/json',
  params = {}
) {
  return new Promise((resolve, reject) => {
    axios
      .put(url, data, {
        params: params,
        headers: {
          'content-type': contentType
        },
        cancelToken: cancelToken,
        responseType: responseType
      })
      .then(
        (response) => {
          resolve(response)
        },
        (err) => {
          reject(err)
        }
      )
  })
}
export async function deleted (
  url,
  params = {},
  cancelToken = null,
  responseType = '',
  contentType = 'application/json'
) {
  return new Promise((resolve, reject) => {
    axios
      .delete(url, {
        params: params,
        headers: {
          'content-type': contentType
        },
        cancelToken: cancelToken,
        responseType: responseType
      })
      .then((response) => {
        resolve(response)
      })
      .catch((err) => {
        reject(err)
      })
  })
}

export const HttpAccount = {
  Login: function (paramObj) {
    return post('/api/crm/auth/login', paramObj)
  },
  Logout: function (paramObj) {
    return post('/api/crm/auth/logout', paramObj)
  },
  CheckJwtTime: function (paramObj) {
    return get('/api/crm/auth', paramObj)
  }
}

// EGKCRM-514 PR-2 Permission Management
export const HttpDepartment = {
  GetAll: function (onlyActive = false) {
    return get('/api/crm/departments', { onlyActive })
  },
  GetOne: function (id) {
    return get(`/api/crm/departments/${id}`)
  },
  Create: function (data) {
    return post('/api/crm/departments', data)
  },
  Update: function (id, data) {
    return put(`/api/crm/departments/${id}`, data)
  },
  Delete: function (id) {
    return deleted(`/api/crm/departments/${id}`)
  }
}

export const HttpGroup = {
  GetAll: function (onlyActive = false) {
    return get('/api/crm/groups', { onlyActive })
  },
  GetOne: function (id) {
    return get(`/api/crm/groups/${id}`)
  },
  Create: function (data) {
    return post('/api/crm/groups', data)
  },
  Update: function (id, data) {
    return put(`/api/crm/groups/${id}`, data)
  },
  Delete: function (id) {
    return deleted(`/api/crm/groups/${id}`)
  },
  UpdateDepartmentBinding: function (id, departmentIds) {
    return put(`/api/crm/groups/${id}/departments`, { departmentIds })
  }
}

export const HttpUserAssignment = {
  GetDepartments: function (userId) {
    return get(`/api/crm/users/${userId}/departments`)
  },
  SetDepartments: function (userId, assignments) {
    return put(`/api/crm/users/${userId}/departments`, { assignments })
  },
  GetGroups: function (userId) {
    return get(`/api/crm/users/${userId}/groups`)
  },
  SetGroups: function (userId, groupIds) {
    return put(`/api/crm/users/${userId}/groups`, { groupIds })
  }
}

export const HttpResource = {
  GetAll: function () {
    return get('/api/crm/resources')
  },
  ToggleUseLegacy: function (key, useLegacyPermission) {
    return put(`/api/crm/resources/${key}/use-legacy`, { useLegacyPermission })
  }
}

export const HttpPermission = {
  GetDepartmentMatrix: function (departmentId) {
    return get(`/api/crm/permissions/department/${departmentId}`)
  },
  UpsertDepartmentMatrix: function (departmentId, cells) {
    return put(`/api/crm/permissions/department/${departmentId}`, { cells })
  },
  GetGroupMatrix: function (groupId) {
    return get(`/api/crm/permissions/group/${groupId}`)
  },
  UpsertGroupMatrix: function (groupId, cells) {
    return put(`/api/crm/permissions/group/${groupId}`, { cells })
  },
  GetUserOverrides: function (userId) {
    return get(`/api/crm/users/${userId}/permission-overrides`)
  },
  UpsertUserOverride: function (userId, data) {
    return post(`/api/crm/users/${userId}/permission-overrides`, data)
  },
  DeleteUserOverride: function (userId, resourceKey, action) {
    return deleted(`/api/crm/users/${userId}/permission-overrides/${resourceKey}/${action}`)
  },
  ClearAllUserOverrides: function (userId) {
    return deleted(`/api/crm/users/${userId}/permission-overrides`)
  },
  GetEffectivePermissions: function (userId) {
    return get(`/api/crm/users/${userId}/effective-permissions`)
  }
}

export const HttpUser = {
  GetUsers: function (paramObj) {
    return get('/api/crm/user', paramObj)
  },
  UpdateUser: function (paramObj) {
    return put('/api/crm/user', paramObj)
  },
  AddUser: function (paramObj) {
    return post('/api/crm/user', paramObj)
  },
  GetEngineerUser: function (paramObj) {
    return get('/api/crm/user/engineer', paramObj)
  },
  GetOneUser: function (paramObj) {
    return get('/api/crm/user/one', paramObj)
  },
  DeleteUser: function (paramObj) {
    return deleted(`/api/crm/user/${paramObj}`, paramObj)
  },
  UpdatePersonalDetail: function (paramObj) {
    return put('/api/crm/user/personal', paramObj)
  }
}

export const HttpRole = {
  GetRole: function (paramObj) {
    return get('/api/crm/role', paramObj)
  }
}

export const HttpItem = {
  GetAllItems: function (paramObj, cancelToken = null) {
    return get('/api/crm/item', paramObj, cancelToken)
  },
  UpdateItem: function (paramObj, cancelToken = null) {
    return put('/api/crm/item/', paramObj, cancelToken)
  },
  GetDataTableItemCount: function (paramObj) {
    return get('/api/crm/item/data-table-item-count', paramObj)
  },
  GetDataTableItem: function (paramObj) {
    return get('/api/crm/item/data-table-item', paramObj)
  },
  GetQuotationItemRepairPrice: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/quotation-price', paramObj, cancelToken)
  },
  GetFilterAllTypeItemCount: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/filter-all-type-count', paramObj, cancelToken)
  },
  GetAllItemCount: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/all-type-count', paramObj, cancelToken)
  },
  GetQuotationItemList: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/quotation-list', paramObj, cancelToken)
  },
  GetItemsList: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/list', paramObj, cancelToken)
  },
  GetEgItemList: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/eg', paramObj, cancelToken)
  },
  GetEgItemGroupList: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/eg-item-group-list', paramObj, cancelToken)
  },
  GetEgItemGroupListCount: function (paramObj) {
    return get('/api/crm/item/eg-item-group-list-count', paramObj)
  },
  GetMonthItem: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/month', paramObj, cancelToken)
  },
  GetPayItem: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/pay', paramObj, cancelToken)
  },
  GetNewItem: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/new', paramObj, cancelToken)
  },
  GetNewItemCount: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/new-count', paramObj, cancelToken)
  },
  GetFilterTypeItemAndAuthorize: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/filter-item-authorize', paramObj, cancelToken)
  },
  GetFilterTypeItemAndAuthorizeCount: function (paramObj, cancelToken = null) {
    return get('/api/crm/item/filter-item-authorize-count', paramObj, cancelToken)
  },
  GetItemById: function (paramObj) {
    return get(`/api/crm/item/id`, paramObj)
  },
  GetItemListWithCustomerDeviceType: function (paramObj) {
    return get(`/api/crm/item/list-cdt`, paramObj)
  },
  GetFilterAllTypeItemCountWithCustomerDeviceType: function (paramObj) {
    return get(`/api/crm/item/filter-all-type-cdt-count`, paramObj)
  }
}

export const HttpBom = {
  GetAllBoms: function (paramObj, cancelToken = null) {
    return get('/api/crm/bom', paramObj, cancelToken)
  },
  GetBomList: function (paramObj, cancelToken = null) {
    return get('/api/crm/bom/list', paramObj, cancelToken)
  },
  GetRepairPriceBomListFromCustomerDevice: function (paramObj, cancelToken = null) {
    return get('/api/crm/bom/repair-price-bom-list-customer-device', paramObj, cancelToken)
  },
  GetRepairPriceBomAndVBomListFromCustomerDevice: function (paramObj, cancelToken = null) {
    return get('/api/crm/bom/repair-price-bom-vbom-list-customer-device', paramObj, cancelToken)
  },
  GetBomListFromCustomerDevice: function (paramObj, cancelToken = null) {
    return get('/api/crm/bom/bom-list-customer-device', paramObj, cancelToken)
  },
  UpdateBom: function (paramObj, cancelToken = null) {
    return put(`/api/crm/bom`, paramObj, cancelToken)
  },
  AddBom: function (paramObj, cancelToken = null) {
    return post('/api/crm/bom', paramObj, cancelToken)
  },
  DeleteBom: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/bom/${paramObj}`, paramObj, cancelToken)
  },
  GetBomById: function (paramObj, cancelToken = null) {
    return get(`/api/crm/bom/bom-id`, paramObj, cancelToken)
  }
}

export const HttpVirtualBom = {
  GetAllVirtualBom: function (paramObj, cancelToken = null) {
    return get('/api/crm/virtual-bom', paramObj, cancelToken)
  },
  UpdateVirtualBom: function (paramObj, cancelToken = null) {
    return put(`/api/crm/virtual-bom`, paramObj, cancelToken)
  },
  AddVirtualBom: function (paramObj, cancelToken = null) {
    return post('/api/crm/virtual-bom', paramObj, cancelToken)
  },
  DeleteVirtualBom: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/virtual-bom/${paramObj}`, paramObj, cancelToken)
  }
}

export const HttpInstallPosition = {
  GetAllInstallPosition: function (paramObj, cancelToken = null) {
    return get('/api/crm/install-position', paramObj, cancelToken)
  },
  UpdateInstallPosition: function (paramObj, cancelToken = null) {
    return put(`/api/crm/install-position`, paramObj, cancelToken)
  },
  AddInstallPosition: function (paramObj, cancelToken = null) {
    return post('/api/crm/install-position', paramObj, cancelToken)
  },
  DeleteInstallPosition: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/install-position/${paramObj}`, paramObj, cancelToken)
  },
  AddInstallPositionFromApplicationForm: function (paramObj, cancelToken = null) {
    return post('/api/crm/install-position/from-application-form', paramObj, cancelToken)
  }
}

export const HttpInstallPositionApplicationForm = {
  GetAllInstallPositionApplicationForm: function (paramObj, cancelToken = null) {
    return get('/api/crm/install-position-application-form', paramObj, cancelToken)
  },
  UpdateInstallPositionApplicationForm: function (paramObj, cancelToken = null) {
    return put(`/api/crm/install-position-application-form`, paramObj, cancelToken)
  },
  AddInstallPositionApplicationForm: function (paramObj, cancelToken = null) {
    return post('/api/crm/install-position-application-form', paramObj, cancelToken)
  },
  DeleteInstallPositionApplicationForm: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/install-position-application-form/${paramObj}`, paramObj, cancelToken)
  },
  UpdateInstallPositionApplicationFormStatus: function (paramObj, cancelToken = null) {
    return put(`/api/crm/install-position-application-form/status`, paramObj, cancelToken)
  },
  UpdateInvalid: function (paramObj, cancelToken = null) {
    return put(`/api/crm/install-position-application-form/invalid`, paramObj, cancelToken)
  },
  GetInstallPositionApplicationFormByNumber: function (paramObj, cancelToken = null) {
    return get(`/api/crm/install-position-application-form/number`, paramObj, cancelToken)
  }
}

export const HttpReviewApplicationForm = {
  GetAllReviewApplicationForm: function (paramObj, cancelToken = null) {
    return get('/api/crm/review-application-form', paramObj, cancelToken)
  },
  UpdateReviewApplicationForm: function (paramObj, cancelToken = null) {
    return put(`/api/crm/review-application-form`, paramObj, cancelToken)
  },
  AddReviewApplicationForm: function (paramObj, cancelToken = null) {
    return post('/api/crm/review-application-form', paramObj, cancelToken)
  },
  DeleteReviewApplicationForm: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/review-application-form/${paramObj}`, paramObj, cancelToken)
  },
  UpdateReviewApplicationFormStatus: function (paramObj, cancelToken = null) {
    return put(`/api/crm/review-application-form/status`, paramObj, cancelToken)
  },
  GetReviewApplicationFormByNumberAndUserName: function (paramObj, cancelToken = null) {
    return get(`/api/crm/review-application-form/one`, paramObj, cancelToken)
  }
}

export const HttpMonthBom = {
  GetAllMonthBoms: function (paramObj, cancelToken = null) {
    return get('/api/crm/month-bom', paramObj, cancelToken)
  },
  UpdateMonthBom: function (paramObj, cancelToken = null) {
    return put(`/api/crm/month-bom`, paramObj, cancelToken)
  },
  AddMonthBom: function (paramObj, cancelToken = null) {
    return post('/api/crm/month-bom', paramObj, cancelToken)
  },
  DeleteMonthBom: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/month-bom/${paramObj}`, paramObj, cancelToken)
  }
}

export const HttpDivideQuotation = {
  DivideQuotation: function (paramObj, cancelToken = null) {
    return post('/api/crm/divide-quotation', paramObj, cancelToken)
  }
}

// 匯出報價單 Excel 共用 helper，isPreview=true 時後端不更新 IsExportQuotation 狀態
function exportQuotationExcel (endpoint, paramObj, isPreview) {
  const url = isPreview
    ? `/api/crm/quotation/${endpoint}?isPreview=true`
    : `/api/crm/quotation/${endpoint}`
  return post(url, paramObj, null, 'blob')
}

export const HttpQuotation = {
  GetAllQuotations: function (paramObj, cancelToken = null) {
    return get(`/api/crm/quotation`, paramObj, cancelToken)
  },
  AddQuotation: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation', paramObj, cancelToken)
  },
  UpdateQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/quotation', paramObj, cancelToken)
  },
  DeleteQuotation: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/quotation/${paramObj}`, paramObj, cancelToken)
  },
  GetAllQuotationTypes: function (paramObj, cancelToken = null) {
    return get(`/api/crm/quotation/type`, paramObj, cancelToken)
  },
  GetAllQuotationCount: function (paramObj, cancelToken = null) {
    return get(`/api/crm/quotation/count`, paramObj, cancelToken)
  },
  // isPreview=true 時後端不更新 IsExportQuotation 狀態
  ExportInstallQuotation: (paramObj, isPreview = false) =>
    exportQuotationExcel('install-excel', paramObj, isPreview),
  ExportProjectPayItemQuotation: (paramObj, isPreview = false) =>
    exportQuotationExcel('project-pay-item-excel', paramObj, isPreview),
  ExportOffsetQuotation: (paramObj, isPreview = false) =>
    exportQuotationExcel('offset-excel', paramObj, isPreview),
  ExportRemoveQuotation: (paramObj, isPreview = false) =>
    exportQuotationExcel('remove-excel', paramObj, isPreview),
  ExportReturnQuotation: (paramObj, isPreview = false) =>
    exportQuotationExcel('return-excel', paramObj, isPreview),
  ExportMoveQuotation: (paramObj, isPreview = false) =>
    exportQuotationExcel('move-excel', paramObj, isPreview),
  ExportTryInstallQuotation: (paramObj, isPreview = false) =>
    exportQuotationExcel('try-install-excel', paramObj, isPreview),
  SearchEditedQuotation: function (paramObj) {
    return get('/api/crm/quotation/search-quotation', paramObj)
  },
  TransferToSale: function (paramObj) {
    return post('/api/crm/quotation/transfer-sunlike-sale', paramObj)
  },
  TransferAcceptanceToResume: function (paramObj) {
    return post('/api/crm/quotation/offset-transfer-acceptance-resume', paramObj)
  },
  TransferRepairToResume: function (paramObj) {
    return post('/api/crm/quotation/offset-transfer-repair-resume', paramObj)
  },
  MakeNumber: function (paramObj) {
    return get('/api/crm/quotation/make', paramObj)
  },
  GetQuotationTerm: function (paramObj) {
    return get('/api/crm/quotation/term', paramObj)
  },
  GetRemoveReasonTypes: function (paramObj) {
    return get('/api/crm/quotation/remove-reason', paramObj)
  },
  GetCustomerDeviceType: function (paramObj) {
    return get(`/api/crm/quotation/customer-device`, paramObj)
  },
  GetInvoiceType: function (paramObj) {
    return get(`/api/crm/quotation/invoice`, paramObj)
  },
  ThrowQuotation: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation/project-transfer-quotation', paramObj, cancelToken)
  },
  CheckHasRepairPrice: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation/check-price', paramObj, cancelToken)
  },
  CheckRepairQuotationIsExist: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation/check-number-exist', paramObj, cancelToken)
  },
  GetIsConfirmTryInstallQuotations: function (paramObj, cancelToken = null) {
    return get('/api/crm/quotation/try-install', paramObj, cancelToken)
  },
  ThrowTryInstallNewInstallToSale: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation/try-install-transfer-sunlike-sale', paramObj, cancelToken)
  },
  UpdateNotInvalidQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/quotation/not-invalid', paramObj, cancelToken)
  },
  TransferProjectPayItemToSale: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation/project-pay-item-transfer-sale', paramObj, cancelToken)
  },
  QuotationTransferOrder: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation/transfer-order', paramObj, cancelToken)
  },
  QuotationTransferOrderInvalid: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation/transfer-order-invalid', paramObj, cancelToken)
  },
  NewQuotationByTransferOrder: function (paramObj, cancelToken = null) {
    return post(
      '/api/crm/quotation/by-transfer-order',
      null,
      cancelToken,
      '',
      'application/json',
      paramObj
    )
  },
  UpdateQuotationByTransferOrder: function (paramObj, cancelToken = null) {
    return put(
      '/api/crm/quotation/by-transfer-order',
      null,
      cancelToken,
      '',
      'application/json',
      paramObj
    )
  }
}

export const HttpMergeQuotation = {
  GetQuotationListForMergeQuotation: function (paramObj, cancelToken = null) {
    return get(`/api/crm/quotation/for-merge-quotation`, paramObj, cancelToken)
  },
  MergeQuotation: function (paramObj) {
    return post('/api/crm/quotation/merge', paramObj, null, 'blob')
  }
}

export const HttpCustomer = {
  GetAllCustomers: function (paramObj, cancelToken = null) {
    return get('/api/crm/customer', paramObj, cancelToken)
  },
  GetCustomerForAutocompleteList: function (paramObj, cancelToken = null, timeout = 0) {
    return get('/api/crm/customer/autocomplete-list', paramObj, cancelToken, '', 'application/json', timeout)
  },
  GetCustomerForDataTableCount: function (paramObj, cancelToken = null) {
    return get('/api/crm/customer/data-table-count', paramObj, cancelToken)
  },
  GetCustomerForDataTable: function (paramObj, cancelToken = null) {
    return get('/api/crm/customer/data-table', paramObj, cancelToken)
  },
  GetCustomerByNumber: function (paramObj, cancelToken = null) {
    return get('/api/crm/customer/by-number', paramObj, cancelToken)
  }
}

export const HttpFile = {
  UploadFile: function (paramObj) {
    return post('/api/crm/file', paramObj)
  },
  DownloadFile: function (paramObj) {
    return post(`/api/crm/file/${paramObj}`, paramObj, null, 'blob')
  },
  FileUploadRecord: function (paramObj) {
    return post('/api/crm/file/record', paramObj)
  }
}

export const HttpCustomerRepair = {
  GetAllCustomerRepair: function (paramObj) {
    return get('/api/crm/customer-repair', paramObj)
  },
  AddCustomerRepair: function (paramObj, cancelToken = null) {
    return post('/api/crm/customer-repair', paramObj, cancelToken)
  },
  UpdateCustomerRepair: function (paramObj, cancelToken = null) {
    return put('/api/crm/customer-repair', paramObj, cancelToken)
  },
  DeleteCustomerRepair: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/customer-repair/${paramObj}`, paramObj, cancelToken)
  },
  GetAllCustomerRepairCount: function (paramObj) {
    return get('/api/crm/customer-repair/count', paramObj)
  },
  GetOneCustomerRepair: function (paramObj) {
    return get('/api/crm/customer-repair/one', paramObj)
  },
  GetCustomerRepairBomItems: function (paramObj) {
    return get('/api/crm/customer-repair/bom-items', paramObj)
  },
  GetOriginalTypes: function (paramObj) {
    return get('/api/crm/customer-repair/original-type', paramObj)
  },
  GetCustomerRepairBomItemDefault: function (paramObj) {
    return get('/api/crm/customer-repair/bom-item-default', paramObj)
  },
  GetNotInCustomerRepairItems: function (paramObj) {
    return post('/api/crm/customer-repair/by-transfer-order', paramObj)
  }
}

export const HttpInstallationNotice = {
  GetNotices: function (paramObj) {
    return get('/api/crm/notice', paramObj)
  },
  GetNotWorkNotices: function (paramObj) {
    return get('/api/crm/notice/notwork', paramObj)
  },
  GetNoticesCount: function (paramObj) {
    return get('/api/crm/notice/count', paramObj)
  },
  AddNotice: function (paramObj, cancelToken = null) {
    return post('/api/crm/notice', paramObj, cancelToken)
  },
  UpdateNotice: function (paramObj, cancelToken = null) {
    return put('/api/crm/notice', paramObj, cancelToken)
  },
  ThrowToAcceptance: function (paramObj) {
    return post('/api/crm/notice/throw', paramObj)
  },
  UpdateNoticeList: function (paramObj) {
    return put('/api/crm/notice/list', paramObj)
  },
  ThrowManyToAcceptance: function (paramObj) {
    return post('/api/crm/notice/throw-many', paramObj)
  },
  DeleteNotice: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/notice/${paramObj}`, paramObj, cancelToken)
  },
  AddNoticeFromAcceptance: function (paramObj, cancelToken = null) {
    return post('/api/crm/notice/acceptance', paramObj, cancelToken)
  },
  IsBackAcceptance: function (paramObj) {
    return put('/api/crm/notice/back', paramObj)
  },
  UpdateSelectDataList: function (paramObj) {
    return put('/api/crm/notice/select-many-data', paramObj)
  },
  UpdateCancelReason: function (paramObj) {
    return put('/api/crm/notice/cancel', paramObj)
  },
  ThrowToResume: function (paramObj) {
    return put('/api/crm/notice/throw-resume', paramObj)
  }
}

export const HttpInstallationAcceptance = {
  GetAcceptances: function (paramObj) {
    return get('/api/crm/acceptance', paramObj)
  },
  GetAcceptancesCount: function (paramObj) {
    return get('/api/crm/acceptance/count', paramObj)
  },
  AddAcceptance: function (paramObj, cancelToken = null) {
    return post('/api/crm/acceptance', paramObj, cancelToken)
  },
  UpdateAcceptance: function (paramObj, cancelToken = null) {
    return put('/api/crm/acceptance', paramObj, cancelToken)
  },
  SearchEditedAcceptance: function (paramObj) {
    return get('/api/crm/acceptance/search', paramObj)
  },
  UpdateAcceptanceIsDelete: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/acceptance/${paramObj}`, paramObj, cancelToken)
  },
  GetBuyOrRentTypes: function (paramObj, cancelToken = null) {
    return get(`/api/crm/acceptance/type`, paramObj, cancelToken)
  },
  TransferAcceptanceCosmosFlow: function (paramObj, cancelToken = null) {
    return post('/api/crm/acceptance/transfer-flow', paramObj, cancelToken)
  },
  TransferAcceptanceSunlikeFlow: function (paramObj, cancelToken = null) {
    return post('/api/crm/acceptance/transfer-sunlike-flow', paramObj, cancelToken)
  },
  TransferAcceptanceToResume: function (paramObj, cancelToken = null) {
    return post('/api/crm/acceptance/transfer-sunlike-resume', paramObj, cancelToken)
  },
  TransferAcceptanceToMonth: function (paramObj, cancelToken = null) {
    return post('/api/crm/acceptance/transfer-month', paramObj, cancelToken)
  },
  UploadImage: function (paramObj) {
    return post('/api/crm/acceptance/upload-image', paramObj)
  },
  GetImage: function (paramObj) {
    return get('/api/crm/acceptance/get-image', paramObj)
  },
  DeleteImage: function (paramObj) {
    return deleted('/api/crm/acceptance/delete-image', paramObj)
  },
  GetNumbersCount: function (paramObj) {
    return get('/api/crm/acceptance/numbers-count', paramObj)
  },
  GetNumbers: function (paramObj) {
    return get('/api/crm/acceptance/numbers', paramObj)
  },
  UpdateAcceptanceItem: function (paramObj) {
    return put('/api/crm/acceptance/acceptance-item', paramObj)
  }
}

export const HttpSerialNumber = {
  GetSerialNumberFromItemId: function (paramObj) {
    return get('/api/crm/serial-number/item-id', paramObj)
  },
  UpdateSerialNumber: function (paramObj, cancelToken = null) {
    return put('/api/crm/serial-number', paramObj, cancelToken)
  },
  GetSerialNumbers: function (paramObj) {
    return get('/api/crm/serial-number', paramObj)
  },
  GetSerialNumberCount: function (paramObj) {
    return get('/api/crm/serial-number/count', paramObj)
  },
  GetSerialNumberList: function (paramObj) {
    return get('/api/crm/serial-number/list', paramObj)
  },
  GetSerialNumberListForEgCount: function (paramObj) {
    return get('/api/crm/serial-number/list-count', paramObj)
  },
  UpdateSerialNumberForIsDoneByEgWork: function (paramObj, cancelToken = null) {
    return put('/api/crm/serial-number/is-done-eg-work', paramObj, cancelToken)
  },
  UpdateSerialNumberForIsDoneByAcceptance: function (paramObj, cancelToken = null) {
    return put('/api/crm/serial-number/is-done-acceptance', paramObj, cancelToken)
  },
  UpdateSerialNumberForIsDeleteByAcceptance: function (paramObj, cancelToken = null) {
    return put('/api/crm/serial-number/is-delete-acceptance', paramObj, cancelToken)
  }
}

export const HttpPoint = {
  DeletePoint: function (paramObj) {
    return deleted(`/api/crm/point/${paramObj}`, paramObj)
  },
  UpdatePoint: function (paramObj, cancelToken = null) {
    return put('/api/crm/point', paramObj, cancelToken)
  },
  GetPoint: function (paramObj) {
    return get('/api/crm/point', paramObj)
  },
  GetPointTypes: function (paramObj) {
    return get('/api/crm/point/type', paramObj)
  },
  AddPoint: function (paramObj, cancelToken = null) {
    return post('/api/crm/point', paramObj, cancelToken)
  }
}

export const HttpItemGroup = {
  DeleteItemGroup: function (paramObj) {
    return deleted(`/api/crm/item-group/${paramObj}`, paramObj)
  },
  UpdateItemGroup: function (paramObj, cancelToken = null) {
    return put('/api/crm/item-group', paramObj, cancelToken)
  },
  GetItemGroup: function (paramObj) {
    return get('/api/crm/item-group', paramObj)
  },
  AddItemGroup: function (paramObj, cancelToken = null) {
    return post('/api/crm/item-group', paramObj, cancelToken)
  }
}

export const HttpResume = {
  DeleteResume: function (paramObj) {
    return deleted(`/api/crm/resume/${paramObj}`, paramObj)
  },
  UpdateResume: function (paramObj, cancelToken = null) {
    return put('/api/crm/resume', paramObj, cancelToken)
  },
  GetResume: function (paramObj) {
    return get('/api/crm/resume', paramObj)
  },
  GetResumeCount: function (paramObj) {
    return get('/api/crm/resume/count', paramObj)
  },
  AddResume: function (paramObj, cancelToken = null) {
    return post('/api/crm/resume', paramObj, cancelToken)
  },
  GetOneResume: function (paramObj) {
    return get('/api/crm/resume/one', paramObj)
  },
  GetResumeByLicensePlate: function (paramObj) {
    return get('/api/crm/resume/license-plate', paramObj)
  },
  GetResumeItemsDto: function (paramObj) {
    return get('/api/crm/resume/resume-items', paramObj)
  }
}

export const HttpFix = {
  GetFixes: function (paramObj) {
    return get('/api/crm/fix', paramObj)
  },
  GetFixesCount: function (paramObj) {
    return get(`/api/crm/fix/count`, paramObj)
  },
  AddFix: function (paramObj) {
    return post('/api/crm/fix', paramObj)
  },
  UpdateFix: function (paramObj) {
    return put('/api/crm/fix', paramObj)
  },
  UpdateFixDone: function (paramObj) {
    return put('/api/crm/fix/done', paramObj)
  },
  DeleteFix: function (paramObj) {
    return deleted(`/api/crm/fix/${paramObj}`, paramObj)
  },
  GetNotWorkFix: function (paramObj) {
    return get('/api/crm/fix/notwork', paramObj)
  },
  GetFixeType: function (paramObj) {
    return get(`/api/crm/fix/type`, paramObj)
  },
  GetCustodyType: function (paramObj) {
    return get(`/api/crm/fix/custody`, paramObj)
  },
  ThrowManyToRepair: function (paramObj) {
    return post('/api/crm/fix/throw-many', paramObj)
  },
  ThrowToRepair: function (paramObj) {
    return post('/api/crm/fix/throw', paramObj)
  },
  UpdateFixList: function (paramObj) {
    return put('/api/crm/fix/many', paramObj)
  },
  SearchFix: function (paramObj) {
    return get('/api/crm/fix/search', paramObj)
  },
  MakeFixNumber: function (paramObj) {
    return get('/api/crm/fix/make', paramObj)
  },
  GetZoneType: function (paramObj) {
    return get(`/api/crm/fix/zone-type`, paramObj)
  },
  UploadImage: function (paramObj) {
    return post('/api/crm/fix/upload-image', paramObj)
  },
  UploadManyFixImage: function (paramObj) {
    return post('/api/crm/fix/upload-many-image', paramObj)
  },
  UpdateManyFix: function (paramObj) {
    return put('/api/crm/fix/many-fix', paramObj)
  },
  GetImage: function (paramObj) {
    return get('/api/crm/fix/get-image', paramObj)
  },
  DeleteImage: function (paramObj) {
    return deleted('/api/crm/fix/delete-image', paramObj)
  },
  UpdateIsOutOfWarranty: function (paramObj, cancelToken = null) {
    return put('/api/crm/fix/is-out-of-warranty', paramObj, cancelToken)
  },
  IsBackRepair: function (paramObj) {
    return put('/api/crm/fix/back', paramObj)
  },
  AutoMakeWork: function (paramObj) {
    return post('/api/crm/fix/auto', paramObj)
  },
  UpdateCancelReason: function (paramObj) {
    return put('/api/crm/fix/cancel', paramObj)
  },
  ThrowToResume: function (paramObj) {
    return put('/api/crm/fix/throw-resume', paramObj)
  },
  ThrowManyToResume: function (paramObj) {
    return put('/api/crm/fix/throw-many-resume', paramObj)
  },
  GetFixNumberCountByCustomerId: function (paramObj) {
    return get('/api/crm/fix/numbers-count', paramObj)
  },
  GetFixNumberByCustomerId: function (paramObj) {
    return get('/api/crm/fix/numbers', paramObj)
  },
  UpdateManyDoneReason: function (paramObj) {
    return put('/api/crm/fix/many-done-reason', paramObj)
  }
}

export const HttpMobileFix = {
  DeletePhoto: function (fileId) {
    return axios.delete('/api/mobile/fix/photo', {
      data: { fileId },
      headers: { 'content-type': 'application/json' },
      timeout: 30000
    })
  },
  UploadPhoto: function (formData, onUploadProgress = null) {
    return post('/api/mobile/fix/photo', formData, null, '', null, {}, onUploadProgress, 120000)
  },
  Create: function (payload) {
    return post('/api/mobile/fix', payload, null, '', 'application/json', {}, null, 60000)
  },
  GetItemBySerial: function (number) {
    return axios.get('api/mobile/fix/item-by-serial', {
      params: { number: number }
    })
  }
}

export const HttpRepair = {
  GetRepair: function (paramObj) {
    return get('/api/crm/repair', paramObj)
  },
  GetRepairCount: function (paramObj) {
    return get('/api/crm/repair/count', paramObj)
  },
  AddRepair: function (paramObj, cancelToken = null) {
    return post('/api/crm/repair', paramObj, cancelToken)
  },
  UpdateRepair: function (paramObj, cancelToken = null) {
    return put('/api/crm/repair', paramObj, cancelToken)
  },
  UpdateRepairIsDelete: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/repair/${paramObj}`, paramObj, cancelToken)
  },
  SearchEditedRepair: function (paramObj) {
    return get('/api/crm/repair/search', paramObj)
  },
  TransferRepairToMonth: function (paramObj) {
    return post('/api/crm/repair/transfer-month', paramObj)
  },
  NewTransferCosmosFlow: function (paramObj) {
    return post('/api/crm/repair/transfer-cosmos-flow', paramObj)
  },
  NewTransferSunlikeFlow: function (paramObj) {
    return post('/api/crm/repair/transfer-sunlike-flow', paramObj)
  },
  UploadImage: function (paramObj) {
    return post('/api/crm/repair/upload-image', paramObj)
  },
  GetImage: function (paramObj) {
    return get('/api/crm/repair/get-image', paramObj)
  },
  DeleteImage: function (paramObj) {
    return deleted('/api/crm/repair/delete-image', paramObj)
  },
  UpdateIsOutOfWarranty: function (paramObj, cancelToken = null) {
    return put('/api/crm/repair/is-out-of-warranty', paramObj, cancelToken)
  },
  GetRepairNumberCountByCustomerId: function (paramObj) {
    return get('/api/crm/repair/numbers-count', paramObj)
  },
  GetRepairNumberByCustomerId: function (paramObj) {
    return get('/api/crm/repair/numbers', paramObj)
  }
}

export const HttpEngineerWork = {
  GetNotDoneWork: function (paramObj) {
    return get('/api/crm/engineer-work', paramObj)
  },
  GetDoneWork: function (paramObj) {
    return get('/api/crm/engineer-work/done', paramObj)
  },
  GetNotWork: function (paramObj) {
    return get('/api/crm/engineer-work/not-work', paramObj)
  },
  GetMixNoticeFixType: function (paramObj) {
    return get('/api/crm/engineer-work/mix-type', paramObj)
  },
  UpdateWork: function (paramObj) {
    return put('/api/crm/engineer-work', paramObj)
  },
  UpdateWorkAccept: function (paramObj) {
    return put('/api/crm/engineer-work/is-accept', paramObj)
  },
  UpdateRejectReason: function (paramObj) {
    return put('/api/crm/engineer-work/reject', paramObj)
  },
  SearchWork: function (paramObj) {
    return get('/api/crm/engineer-work/search', paramObj)
  },
  UpdateIsOutOfRepairWarranty: function (paramObj, cancelToken = null) {
    return put('/api/crm/engineer-work/update', paramObj, cancelToken)
  },
  GetItemInCar: function (paramObj) {
    return get('/api/crm/engineer-work/item-in-car', paramObj)
  },
  UpdateIsWork: function (paramObj) {
    return put('/api/crm/engineer-work/is-work', paramObj)
  },
  UpdateCancelReason: function (paramObj) {
    return put('/api/crm/engineer-work/cancel', paramObj)
  }
}

export const HttpMonth = {
  GetMonth: function (paramObj) {
    return get('/api/crm/month', paramObj)
  },
  GetMonthCount: function (paramObj) {
    return get('/api/crm/month/count', paramObj)
  },
  AddMonth: function (paramObj, cancelToken = null) {
    return post('/api/crm/month', paramObj, cancelToken)
  },
  UpdateMonth: function (paramObj, cancelToken = null) {
    return put('/api/crm/month', paramObj, cancelToken)
  },
  UpdateMonthIsDelete: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/month/${paramObj}`, paramObj, cancelToken)
  },
  GetUnlimitedMonthList: function (paramObj) {
    return get('/api/crm/month/cid-unlimited', paramObj)
  },
  GetMonthList: function (paramObj) {
    return get('/api/crm/month/cid-license-plate', paramObj)
  },
  GetRenewMonthList: function (paramObj) {
    return get('/api/crm/month/cid-license-plates', paramObj)
  },
  StopMonth: function (paramObj, cancelToken = null) {
    return put('/api/crm/month/stop-month', paramObj, cancelToken)
  },
  GetAllCustomerLicensePlateList: function (paramObj) {
    return get('/api/crm/month/all-customer-licensePlate', paramObj)
  },
  GetMonthStatus: function (paramObj) {
    return get('/api/crm/month/status', paramObj)
  },
  GetMonthItemStatus: function (paramObj) {
    return get('/api/crm/month/item-status', paramObj)
  },
  GetAllPayType: function (paramObj) {
    return get('/api/crm/month/pay-type', paramObj)
  },
  GetAllPayQuantityType: function (paramObj) {
    return get('/api/crm/month/pay-quantity-type', paramObj)
  },
  ThrowMonthFromQuotation: function (paramObj, cancelToken = null) {
    return post('/api/crm/month/throw-month', paramObj, cancelToken)
  },
  GetStopMonthList: function (paramObj) {
    return get('/api/crm/month/stop-month-list', paramObj)
  },
  GetCustomerLicensePlateList: function (paramObj) {
    return get('/api/crm/month/customer-license-plate', paramObj)
  },
  GetCustomerOriginalLicensePlateList: function (paramObj) {
    return get('/api/crm/month/customer-original-license-plate', paramObj)
  },
  NewMonthItems: function (paramObj, cancelToken = null) {
    return post('/api/crm/month/new', paramObj, cancelToken)
  },
  RemoveMonthItems: function (paramObj, cancelToken = null) {
    return post('/api/crm/month/remove', paramObj, cancelToken)
  },
  ReturnMonthItems: function (paramObj, cancelToken = null) {
    return post('/api/crm/month/return', paramObj, cancelToken)
  },
  GetLicensePlateListByCustomerId: function (paramObj) {
    return get('/api/crm/month/license-plates-list-cid', paramObj)
  },
  GetAllLicensePlateList: function (paramObj) {
    return get('/api/crm/month/license-plates-list', paramObj)
  },
  GetAllLicensePlateListCount: function (paramObj) {
    return get('/api/crm/month/license-plates-list-count', paramObj)
  },
  GetMonthExistLicensePlateError: function (paramObj) {
    return post('/api/crm/month/check-license-plate', paramObj)
  },
  GetMonthListByLicensePlate: function (paramObj) {
    return get('/api/crm/month/license-plate', paramObj)
  },
  GetPayTimeRangeTypes: function () {
    return get('/api/crm/month/pay-time-range-type')
  },
  UpdateRenewMonthFromMonthQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/month/renew-mq', paramObj, cancelToken)
  },
  UpdateRenewMonthFromQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/month/renew-q', paramObj, cancelToken)
  },
  UpdateUnlimitedMonth: function (paramObj, cancelToken = null) {
    return put('/api/crm/month/unlimited-month', paramObj, cancelToken)
  }
}

export const HttpSettle = {
  SetSaleDetail: function (paramObj, cancelToken = null) {
    return post('/api/crm/settle', paramObj, cancelToken)
  },
  GetSettleType: function (paramObj) {
    return get(`/api/crm/settle/type`, paramObj)
  },
  GetOverageError: function (paramObj) {
    return get(`/api/crm/settle`, paramObj)
  },
  ProcessUnlimitedMonth: function (paramObj, cancelToken = null) {
    return put('/api/crm/settle/process-unlimited-month', paramObj, cancelToken)
  }
}

export const HttpCmsmq = {
  GetSaleType: function (paramObj) {
    return get(`/api/crm/cmsmq`, paramObj)
  }
}

export const HttpDiscount = {
  GetDiscount: function (paramObj) {
    return get('/api/crm/discount', paramObj)
  },
  GetDiscountCount: function (paramObj) {
    return get('/api/crm/discount/count', paramObj)
  },
  AddDiscount: function (paramObj, cancelToken = null) {
    return post('/api/crm/discount', paramObj, cancelToken)
  },
  UpdateDiscount: function (paramObj, cancelToken = null) {
    return put('/api/crm/discount', paramObj, cancelToken)
  },
  UpdateDiscountIsDelete: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/discount/${paramObj}`, paramObj, cancelToken)
  },
  GetDiscountType: function (paramObj) {
    return get(`/api/crm/discount/type`, paramObj)
  },
  GetDiscountItemType: function (paramObj) {
    return get(`/api/crm/discount/type-item`, paramObj)
  },
  UpdateDiscountWithRemoveQuotation: function (paramObj) {
    return put('/api/crm/discount/remove', paramObj)
  },
  UpdateDiscountWithSettle: function (paramObj) {
    return put('/api/crm/discount/settle', paramObj)
  },
  ThrowDiscountFromQuotation: function (paramObj) {
    return post('/api/crm/discount/throw-discount', paramObj)
  },
  AddNewDiscountWithNewMonthQuotation: function (paramObj) {
    return post('/api/crm/discount/new-month-quotation', paramObj)
  },
  UpdateNewDiscountWithRemoveMonthQuotation: function (paramObj) {
    return put('/api/crm/discount/remove-month-quotation', paramObj)
  },
  UpdateNewDiscountWithReturnMonthQuotation: function (paramObj) {
    return put('/api/crm/discount/return-month-quotation', paramObj)
  },
  AddDiscountWithDiscountMonthQuotation: function (paramObj) {
    return post('/api/crm/discount/discount-month-quotation', paramObj)
  }
}

export const HttpCmsmc = {
  GetStock: function (paramObj) {
    return get(`/api/crm/cmsmc`, paramObj)
  }
}

export const HttpEngineerStock = {
  GetEngineerStock: function (paramObj) {
    return get(`/api/crm/engineer-stock`, paramObj)
  },
  AddEngineerStock: function (paramObj, cancelToken = null) {
    return post('/api/crm/engineer-stock', paramObj, cancelToken)
  },
  UpdateEngineerStock: function (paramObj) {
    return put('/api/crm/engineer-stock', paramObj)
  },
  UpdateEngineerStockIsDelete: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/engineer-stock/${paramObj}`, paramObj, cancelToken)
  },
  GetStockItem: function (paramObj) {
    return get(`/api/crm/engineer-stock/stock`, paramObj)
  }
}

export const HttpCmsmf = {
  GetCurrency: function (paramObj) {
    return get(`/api/crm/cmsmf`, paramObj)
  }
}

export const HttpTpManager = {
  GetTpManager: function (paramObj) {
    return get('/api/crm/tpmanager', paramObj)
  },
  GetTpManagerCount: function (paramObj) {
    return get('/api/crm/tpmanager/count', paramObj)
  },
  GetOneTpManager: function (paramObj) {
    return get('/api/crm/tpmanager/one', paramObj)
  },
  AddTpManager: function (paramObj, cancelToken = null) {
    return post('/api/crm/tpmanager', paramObj, cancelToken)
  },
  UpdateTpManager: function (paramObj, cancelToken = null) {
    return put('/api/crm/tpmanager', paramObj, cancelToken)
  },
  UpdateTpManagerIsDelete: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/tpmanager/${paramObj}`, paramObj, cancelToken)
  }
}

export const HttpSpecialNeed = {
  GetSpecialNeed: function (paramObj) {
    return get('/api/crm/special-need', paramObj)
  },
  AddSpecialNeed: function (paramObj, cancelToken = null) {
    return post('/api/crm/special-need', paramObj, cancelToken)
  },
  UpdateSpecialNeed: function (paramObj, cancelToken = null) {
    return put('/api/crm/special-need', paramObj, cancelToken)
  },
  UpdateSpecialNeedIsDelete: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/special-need/${paramObj}`, paramObj, cancelToken)
  }
}

export const HttpCheckExcel = {
  MonthBomExcel: function (paramObj) {
    return post('/api/crm/check-excel/month-bom', paramObj)
  },
  BomExcel: function (paramObj) {
    return post('/api/crm/check-excel/bom', paramObj)
  },
  NewItemExcel: function (paramObj) {
    return post('/api/crm/check-excel/new-item', paramObj)
  },
  OldItemExcel: function (paramObj) {
    return post('/api/crm/check-excel/old-item', paramObj)
  },
  AstTypeExcel: function (paramObj) {
    return post('/api/crm/check-excel/ast-type', paramObj)
  },
  CustomerExcel: function (paramObj) {
    return post('/api/crm/check-excel/customer', paramObj)
  },
  AstmbExcel: function (paramObj) {
    return post('/api/crm/check-excel/astmb', paramObj)
  },
  AstmcExcel: function (paramObj) {
    return post('/api/crm/check-excel/astmc', paramObj)
  },
  SupplierExcel: function (paramObj) {
    return post('/api/crm/check-excel/supplier', paramObj)
  },
  MonthHeadExcel: function (paramObj) {
    return post('/api/crm/check-excel/month-head', paramObj)
  },
  MonthBodyExcel: function (paramObj) {
    return post('/api/crm/check-excel/month-body', paramObj)
  },
  ResumeExcel: function (paramObj) {
    return post('/api/crm/check-excel/resume', paramObj)
  },
  InstallPositionExcel: function (paramObj) {
    return post('/api/crm/check-excel/install-position', paramObj)
  },
  SunlikeNewItemExcel: function (paramObj) {
    return post('/api/crm/check-excel/sunlike-new-item', paramObj)
  },
  SunlikeOldItemExcel: function (paramObj) {
    return post('/api/crm/check-excel/sunlike-old-item', paramObj)
  }
}

export const HttpMonthQuotation = {
  GetMonthQuotations: function (paramObj, cancelToken = null) {
    return get(`/api/crm/month-quotation`, paramObj, cancelToken)
  },
  GetMonthQuotationCount: function (paramObj, cancelToken = null) {
    return get(`/api/crm/month-quotation/count`, paramObj, cancelToken)
  },
  AddMonthQuotation: function (paramObj, cancelToken = null) {
    return post('/api/crm/month-quotation', paramObj, cancelToken)
  },
  UpdateMonthQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/month-quotation', paramObj, cancelToken)
  },
  DeleteMonthQuotation: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/month-quotation/${paramObj}`, paramObj, cancelToken)
  },
  GetMonthQuotationTypes: function (paramObj, cancelToken = null) {
    return get(`/api/crm/month-quotation/type`, paramObj, cancelToken)
  },
  ProcessUnlimitedMonthQuotationList: function (paramObj, cancelToken = null) {
    return put('/api/crm/month-quotation/process-unlimited-month', paramObj, cancelToken)
  }
}

export const HttpChangeDetail = {
  GetChangeDetails: function (paramObj, cancelToken = null) {
    return get(`/api/crm/change-detail`, paramObj, cancelToken)
  },
  AddChangeDetail: function (paramObj, cancelToken = null) {
    return post('/api/crm/change-detail', paramObj, cancelToken)
  },
  UpdateChangeDetail: function (paramObj, cancelToken = null) {
    return put('/api/crm/change-detail', paramObj, cancelToken)
  },
  DeleteChangeDetail: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/change-detail/${paramObj}`, paramObj, cancelToken)
  },
  GetChangeDetailTypes: function (paramObj, cancelToken = null) {
    return get(`/api/crm/change-detail/type`, paramObj, cancelToken)
  },
  ThrowChangeDetail: function (paramObj, cancelToken = null) {
    return put('/api/crm/change-detail/throw', paramObj, cancelToken)
  }
}

export const HttpOverage = {
  GetOverages: function (paramObj, cancelToken = null) {
    return get(`/api/crm/overage`, paramObj, cancelToken)
  },
  GetOverageCount: function (paramObj, cancelToken = null) {
    return get(`/api/crm/overage/count`, paramObj, cancelToken)
  },
  AddOverage: function (paramObj, cancelToken = null) {
    return post('/api/crm/overage', paramObj, cancelToken)
  },
  UpdateOverage: function (paramObj, cancelToken = null) {
    return put('/api/crm/overage', paramObj, cancelToken)
  },
  DeleteOverage: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/overage/${paramObj}`, paramObj, cancelToken)
  },
  ThrowOverageFromQuotation: function (paramObj, cancelToken = null) {
    return post('/api/crm/overage/throw-overage', paramObj, cancelToken)
  },
  AddOverageFromMonthQuotation: function (paramObj, cancelToken = null) {
    return post('/api/crm/overage/month-quotation', paramObj, cancelToken)
  },
  UpdateOverageFromRemoveMonthQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/overage/remove-month-quotation', paramObj, cancelToken)
  },
  UpdateOverageFromReturnMonthQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/overage/return-month-quotation', paramObj, cancelToken)
  },
  UpdateOverageFromRemoveQuotation: function (paramObj, cancelToken = null) {
    return put('/api/crm/overage/remove-quotation', paramObj, cancelToken)
  },
  UpdateOverageWithSettle: function (paramObj) {
    return put('/api/crm/overage/settle', paramObj)
  }
}

export const HttpQuotationChangeDetail = {
  GetQuotationChangeDetails: function (paramObj, cancelToken = null) {
    return get(`/api/crm/quotation-change-detail`, paramObj, cancelToken)
  },
  AddQuotationChangeDetail: function (paramObj, cancelToken = null) {
    return post('/api/crm/quotation-change-detail', paramObj, cancelToken)
  },
  UpdateQuotationChangeDetail: function (paramObj, cancelToken = null) {
    return put('/api/crm/quotation-change-detail', paramObj, cancelToken)
  },
  DeleteQuotationChangeDetail: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/quotation-change-detail/${paramObj}`, paramObj, cancelToken)
  },
  ThrowQuotationChangeDetail: function (paramObj, cancelToken = null) {
    return put('/api/crm/quotation-change-detail/throw', paramObj, cancelToken)
  }
}

export const HttpPageChangeRecord = {
  GetAllRecordCount: function (paramObj) {
    return get('/api/crm/page-change-record/count', paramObj)
  },
  GetAllRecord: function (paramObj) {
    return get('/api/crm/page-change-record', paramObj)
  },
  GetPageTypes: function (paramObj) {
    return get('/api/crm/page-change-record/page-type', paramObj)
  },
  GetActionTypes: function (paramObj) {
    return get('/api/crm/page-change-record/action-type', paramObj)
  },
  AddCopyQuotationRecord: function (paramObj, cancelToken = null) {
    return post('/api/crm/page-change-record', paramObj, cancelToken)
  }
}

export const HttpUpdateCustomerRepair = {
  AddCustomerRepair: function (paramObj, cancelToken = null) {
    return post('/api/crm/update-customer-repair', paramObj, cancelToken)
  },
  UpdateCustomerRepair: function (paramObj, cancelToken = null) {
    return put('/api/crm/update-customer-repair', paramObj, cancelToken)
  },
  DeleteCustomerRepair: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/update-customer-repair/${paramObj}`, paramObj, cancelToken)
  },
  GetCustomerRepairItems: function (paramObj, cancelToken = null) {
    return get('/api/crm/update-customer-repair/items', paramObj, cancelToken)
  },
  GetCustomerRepairBoms: function (paramObj, cancelToken = null) {
    return get('/api/crm/update-customer-repair/boms', paramObj, cancelToken)
  },
  GetCustomerRepairBomItems: function (paramObj, cancelToken = null) {
    return get('/api/crm/update-customer-repair/bom-items', paramObj, cancelToken)
  },
  GetUpdateCustomerRepairDtoAsync: function (paramObj, cancelToken = null) {
    return get('/api/crm/update-customer-repair', paramObj, cancelToken)
  },
  GetUpdateCustomerRepairCount: function (paramObj, cancelToken = null) {
    return get('/api/crm/update-customer-repair/count', paramObj, cancelToken)
  },
  ThrowUpdateCustomerRepair: function (paramObj, cancelToken = null) {
    return post('/api/crm/update-customer-repair/transfer', paramObj, cancelToken)
  }
}

export const HttpAuthorizeMapping = {
  GetAuthorizeMapping: function (paramObj) {
    return get('/api/crm/authorize-mapping', paramObj)
  },
  GetAuthorizeMappingCount: function (paramObj) {
    return get('/api/crm/authorize-mapping/count', paramObj)
  },
  AddAuthorizeMapping: function (paramObj, cancelToken = null) {
    return post('/api/crm/authorize-mapping', paramObj, cancelToken)
  },
  UpdateAuthorizeMapping: function (paramObj, cancelToken = null) {
    return put('/api/crm/authorize-mapping', paramObj, cancelToken)
  },
  DeleteAuthorizeMapping: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/authorize-mapping/${paramObj}`, paramObj, cancelToken)
  },
  GetGmTypes: function (paramObj) {
    return get('/api/crm/authorize-mapping/type', paramObj)
  },
  GetAuthorizeMappingByItemIdAndItemSerialNumber: function (paramObj) {
    return get('/api/crm/authorize-mapping/item-serial-number', paramObj)
  }
}

export const HttpAcceptanceTryInstallDivide = {
  GetTryInstallDividesListByAcceptanceNumber: function (paramObj) {
    return get('/api/crm/acceptance-try-install-divide', paramObj)
  },
  AddAcceptanceTryInstallDivide: function (paramObj, cancelToken = null) {
    return post('/api/crm/acceptance-try-install-divide', paramObj, cancelToken)
  },
  UpdateAcceptanceTryInstallDivide: function (paramObj, cancelToken = null) {
    return put('/api/crm/acceptance-try-install-divide', paramObj, cancelToken)
  },
  DeleteAcceptanceTryInstallDivide: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/acceptance-try-install-divide/${paramObj}`, paramObj, cancelToken)
  },
  GetTryInstallDivideTypeList: function () {
    return get('/api/crm/acceptance-try-install-divide/type')
  },
  DivideInstallOrRemoveFlow: function (paramObj, cancelToken = null) {
    return post('/api/crm/acceptance-try-install-divide/divide', paramObj, cancelToken)
  },
  UpdateInvalidDivideInstallOrRemove: function (paramObj, cancelToken = null) {
    return put('/api/crm/acceptance-try-install-divide/invalid', paramObj, cancelToken)
  }
}

export const HttpCustomerLicensePlateMapping = {
  GetCustomerLicensePlateMappingDataTable: function (paramObj) {
    return get('/api/crm/customer-license-plate-mapping', paramObj)
  },
  GetCustomerLicensePlateMappingList: function (paramObj) {
    return get('/api/crm/customer-license-plate-mapping/list', paramObj)
  },
  GetCustomerLicensePlateMappingCount: function (paramObj) {
    return get('/api/crm/customer-license-plate-mapping/count', paramObj)
  },
  GetLicensePlateListByCustomerId: function (paramObj) {
    return get('/api/crm/customer-license-plate-mapping/customer-id', paramObj)
  }
}

export const HttpCheckMonthExcelPay = {
  CheckMonthExcelPay: function (paramObj) {
    return post('/api/crm/check-month-excel-pay', paramObj, null, 'blob')
  }
}

export const HttpTempStock = {
  GetTempStockForDataTable: function (paramObj, cancelToken = null) {
    return get('/api/crm/temp-stock', paramObj, cancelToken)
  },
  GetTempStockForDataTableCount: function (paramObj, cancelToken = null) {
    return get('/api/crm/temp-stock/count', paramObj, cancelToken)
  },
  AddTempStock: function (paramObj, cancelToken = null) {
    return post('/api/crm/temp-stock', paramObj, cancelToken)
  },
  UpdateTempStock: function (paramObj, cancelToken = null) {
    return put('/api/crm/temp-stock', paramObj, cancelToken)
  },
  DeleteTempStock: function (paramObj, cancelToken = null) {
    return deleted(`/api/crm/temp-stock/${paramObj}`, paramObj, cancelToken)
  },
  ImportTempStock: function (paramObj, cancelToken = null) {
    return post('/api/crm/temp-stock/import', paramObj, cancelToken)
  },
  GetTempStockByItemNumber: function (paramObj) {
    return get('/api/crm/temp-stock/item-number', paramObj)
  },
  GetTempStockListByItemsNumber: function (paramObj) {
    return get('/api/crm/temp-stock/item-number-list', paramObj)
  },
  UpdateCurrentQuantityFromQuotation: function (paramObj) {
    return put('/api/crm/temp-stock/quotaiton', paramObj)
  },
  GetLatestUpdateTime: function () {
    return get('/api/crm/temp-stock/latest-update-time')
  }
}

export const HttpUpdateManyCustomerRepair = {
  GetUpdateManyCustomerRepairDto (paramObj, cancelToken) {
    return get('/api/crm/update-many-customer-repair', paramObj, cancelToken)
  },
  GetUpdateManyCustomerRepairCount (paramObj, cancelToken) {
    return get('/api/crm/update-many-customer-repair/count', paramObj, cancelToken)
  },
  AddManyCustomerRepair (paramObj) {
    return post('/api/crm/update-many-customer-repair', paramObj)
  },
  UpdateManyCustomerRepair (paramObj) {
    return put('/api/crm/update-many-customer-repair', paramObj)
  },
  DeleteManyCustomerRepair: function (paramObj) {
    return deleted(`/api/crm/update-many-customer-repair/${paramObj}`, paramObj)
  },
  ComfirmUpdateManyCustomerRepair: function (paramObj) {
    return put('/api/crm/update-many-customer-repair/throw', paramObj)
  }
}

export const HttpSunlike = {
  ThrowSunlikeSale: function (paramObj) {
    return post('/api/crm/sunlike/throw-sale', paramObj)
  }
}

export const HttpTransferOrder = {
  GetTransferOrders: function (paramObj) {
    return get('/api/crm/transfer-order', paramObj)
  },
  ThrowTransferOrder: function (paramObj) {
    return post('/api/crm/transfer-order', paramObj)
  },
  UpdateTransferOrder: function (paramObj) {
    return put('/api/crm/transfer-order', paramObj)
  },
  UpdateTransferOrderInvalid: function (paramObj) {
    return post('/api/crm/transfer-order/invalid', paramObj)
  },
  GetTransferOrderNumberStatusIsDone: function (paramObj) {
    return get('/api/crm/transfer-order/status', paramObj)
  },
  GetTransferOrderByNumber: function (paramObj) {
    return get('/api/crm/transfer-order/by-number', paramObj)
  },
  // 新增：狀態轉換專用 API（後端驗證狀態轉換是否合法）
  SubmitToSale: function (id) {
    return post(`/api/crm/transfer-order/submit-to-sale/${id}`)
  },
  ReturnToStock: function (id) {
    return post(`/api/crm/transfer-order/return-to-stock/${id}`)
  },
  // 原子性操作：轉拋 ERP + 更新報價單（單一交易）
  ThrowAndUpdateQuotation: function (paramObj) {
    return post('/api/crm/transfer-order/throw-and-update', paramObj)
  },
  // 原子性操作：作廢調撥單 + 作廢報價單 + 重開報價單（單一交易）
  InvalidAndReopenQuotation: function (paramObj) {
    return post('/api/crm/transfer-order/invalid-and-reopen', paramObj)
  }
}
